import React, { Dispatch, FC, SetStateAction, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Checkbox,
  FontIcon,
  IColumn,
  IconButton,
  IContextualMenuItemProps,
  ScrollablePane,
  Stack,
  Text,
  TextBadge,
  TextBadgeColor,
  TextBadgeSize,
  TooltipHost,
  useTheme
} from "@bps/fluent-ui";
import { CopyableGuid } from "@components/CopyableText";
import { ExpandableRow } from "@components/tables/ExpandableRow";
import { ShimmeredDetailsList } from "@components/tables/ShimmeredDetailsList";
import { useExpandedRows } from "@components/tables/useExpandedRows";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import {
  ApplicationType,
  ContactCommsType,
  PltUser,
  Tenant,
  TenantUserSortField
} from "@libs/api/gateways/plt/plt-gateway.dtos";
import {
  useSearchUsers,
  useUpdateUser
} from "@libs/api/gateways/plt/plt-gateway.hooks";
import { NO_ACTION_PERMISSION } from "@libs/permissions/permissions.constants";
import { Permissions } from "@libs/permissions/permissions.enum";

import { InviteFormDialog } from "../../../../../_shared-blocks/InviteFormDialog";
import { TenantUsersFilters } from "../TenantUsersScreen";
import { AdditionalUserInformationRow } from "./AdditionalUserInformationRow";
import { TenantUserStatusBadge } from "./TenantUserStatusBadge";

interface PltUsersTableProps {
  tenant: Tenant;
  filter: TenantUsersFilters;
  selectedUsersIds: Set<string>;
  setSelectedUsersIds: Dispatch<SetStateAction<Set<string>>>;
  showNotificationsDialog: () => void;
}

enum UserListKeys {
  Actions = "actions",
  Selector = "check",
  UserId = "userId",
  FirstName = "firstName",
  LastName = "lastName",
  UserName = "username",
  Email = "email",
  Application = "applicationCode",
  HasBpId = "hasBpId",
  HasExternalId = "hasExternalId",
  Status = "isInactive",
  SortField = "sortField",
  SortAscending = "sortAscending"
}

export const PltUsersTable: FC<PltUsersTableProps> = ({
  tenant,
  filter,
  selectedUsersIds,
  setSelectedUsersIds,
  showNotificationsDialog
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isPremierTenant = tenant.application === ApplicationType.PROS;

  const [sortField, setSortField] = useState<
    TenantUsersFilters[UserListKeys.SortField]
  >();

  const [sortAscending, setSortAscending] = useState<
    TenantUsersFilters[UserListKeys.SortAscending]
  >();

  const getIsSortedDescending = (field: TenantUserSortField | undefined) => {
    if (sortField === undefined) return undefined;
    if (field === undefined) return undefined;
    if (sortAscending === undefined) return undefined;
    return sortField === field && !!!sortAscending;
  };

  const onColumnClick = (
    field: TenantUserSortField | undefined
  ): IColumn["onColumnClick"] => (
    _ev: React.MouseEvent<HTMLElement>,
    column: IColumn
  ) => {
    setSortField(field);
    column.isSortedDescending = !!getIsSortedDescending(field);
    setSortAscending(column.isSortedDescending);
  };

  const [inviteUserId, setInviteUserId] = useState<string | undefined>();

  // Update the BpIdUserId filter

  const { isLoading, error, data = [] } = useSearchUsers({
    ...filter,
    bpIdUserId: filter.bpIdUserId || undefined,
    userStatusCode: filter.userStatusCode || undefined,
    hasBpId: filter.hasBpId ? JSON.parse(filter.hasBpId) : undefined,
    hasExternalId: filter.hasExternalId
      ? JSON.parse(filter.hasExternalId)
      : undefined,
    isInactive: filter.isInactive ? JSON.parse(filter.isInactive) : undefined,
    tenantId: tenant.id,
    sortField,
    sortAscending
  });

  const userToInvite = useMemo(() => {
    if (!inviteUserId || !data.length) return undefined;
    return data.find(user => user.id === inviteUserId);
  }, [data, inviteUserId]);

  const {
    isLoading: isUpdatedUserLoading,
    mutateAsync: updateUser
  } = useUpdateUser();

  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltCatalogUserWrite
  );

  const { data: hasInvitationPermission } = usePermissionsCheckQuery([
    Permissions.PltInvitationWrite,
    Permissions.PltInvitationRead
  ]);

  const { data: hasNotificationPermission } = usePermissionsCheckQuery(
    Permissions.PltNotificationsWrite
  );

  const { expandedIds, toggleExpandedIds } = useExpandedRows();

  const handleSelectedUsersIds = (id: string) => {
    setSelectedUsersIds(prev =>
      prev.has(id)
        ? new Set([...prev].filter(x => x !== id))
        : new Set(prev.add(id))
    );
  };

  const renderItem = (
    props: IContextualMenuItemProps,
    tooltipContent: string | undefined
  ) => {
    if (!props.item.disabled)
      return (
        <Text styles={{ root: { padding: "0px 4px" } }}>{props.item.text}</Text>
      );

    return (
      <TooltipHost
        content={tooltipContent}
        styles={{ root: { padding: "0px 4px" } }}
      >
        <Stack
          horizontal
          verticalAlign="center"
          horizontalAlign="center"
          tokens={{ childrenGap: 8 }}
        >
          <Text
            styles={{
              root: {
                color: props.item.disabled
                  ? theme.semanticColors.disabledText
                  : "initial"
              }
            }}
          >
            {props.item.text}
          </Text>
          <FontIcon
            iconName="Info"
            styles={{
              root: { color: theme.semanticColors.severeWarningIcon }
            }}
          />
        </Stack>
      </TooltipHost>
    );
  };

  const columns: IColumn[] = [
    {
      key: UserListKeys.Actions,
      name: "",
      maxWidth: 32,
      minWidth: 32,
      onRender: (item: PltUser) => {
        const tenantMismatch = item?.tenantId !== tenant.id;

        const disableInvite =
          !hasInvitationPermission || tenantMismatch || !!item?.bpIdUserId;

        const getInviteDisableTooltipContent = () => {
          if (!hasInvitationPermission) return NO_ACTION_PERMISSION;
          if (tenantMismatch)
            return "CAM User cannot be invited to Child Tenant";
          if (!!item?.bpIdUserId) return "User has BpID account already.";
          if (tenant.isInactive) return "Tenant is not active.";

          return undefined;
        };

        return (
          <IconButton
            styles={{ root: { justifySelf: "end" } }}
            menuIconProps={{ iconName: "more" }}
            menuProps={{
              items: [
                {
                  key: "notification",
                  text: "Send notification",
                  onClick: () => {
                    handleSelectedUsersIds(item.id);
                    showNotificationsDialog();
                  },
                  disabled: !hasNotificationPermission,
                  onRenderContent: props =>
                    renderItem(props, NO_ACTION_PERMISSION),
                  itemProps: {
                    styles: { root: { pointerEvents: "auto" } }
                  }
                },
                {
                  key: "edit",
                  text: hasWritePermission ? "Edit/more" : "More",
                  onClick: () => navigate(`./${item.id}`)
                },
                {
                  key: "statusUpdate",
                  text: !item.isInactive ? "Disable" : "Enable",
                  onClick: () => {
                    updateUser({
                      ...item,
                      isInactive: !item.isInactive
                    });
                  },
                  disabled: isUpdatedUserLoading || !hasWritePermission,
                  onRenderContent: props =>
                    renderItem(props, NO_ACTION_PERMISSION),
                  itemProps: {
                    styles: { root: { pointerEvents: "auto" } }
                  }
                },
                {
                  key: "invite",
                  text: "Invite",
                  onClick: () => {
                    setInviteUserId(item.id);
                  },
                  disabled:
                    isUpdatedUserLoading || disableInvite || tenant.isInactive,
                  onRenderContent: props =>
                    renderItem(props, getInviteDisableTooltipContent()),
                  itemProps: {
                    styles: { root: { pointerEvents: "auto" } }
                  }
                }
              ]
            }}
          />
        );
      }
    },
    {
      key: UserListKeys.Selector,
      name: "",
      maxWidth: 35,
      minWidth: 35,
      onRenderHeader: () => (
        <Checkbox
          automationAttribute="check-for-notifications-all"
          checked={data.length > 0 && data.length === selectedUsersIds.size}
          onChange={(ev, checked) => {
            ev?.stopPropagation();
            if (!checked) {
              setSelectedUsersIds(new Set());
            } else {
              setSelectedUsersIds(new Set(data.map(d => d.id)));
            }
          }}
          styles={{ root: { paddingTop: 8 } }}
        />
      ),
      onRender: (item: PltUser) => (
        <Checkbox
          automationAttribute={`check-for-notification-${item.id}`}
          checked={selectedUsersIds.has(item.id)}
          onChange={ev => {
            ev?.stopPropagation();
            handleSelectedUsersIds(item.id);
          }}
        />
      )
    },
    {
      key: UserListKeys.UserId,
      name: "Id",
      maxWidth: 100,
      minWidth: 100,
      onRender: (item: PltUser) => <CopyableGuid short value={item.id} />
    },
    {
      key: UserListKeys.FirstName,
      name: "First Name",
      maxWidth: 100,
      minWidth: 100,
      onRender: (item: PltUser) => item.firstName,
      isSorted: true,
      isSortedDescending: getIsSortedDescending(UserListKeys.FirstName),
      onColumnClick: onColumnClick(UserListKeys.FirstName)
    },
    {
      key: UserListKeys.LastName,
      name: "Last Name",
      maxWidth: 150,
      minWidth: 200,
      onRender: (item: PltUser) => item.lastName,
      isSorted: true,
      isSortedDescending: getIsSortedDescending(UserListKeys.LastName),
      onColumnClick: onColumnClick(UserListKeys.LastName)
    },
    {
      key: UserListKeys.Status,
      name: "Status",
      minWidth: 90,
      maxWidth: 90,
      onRender: (item: PltUser) => (
        <TenantUserStatusBadge
          isInactive={item.isInactive}
          status={item.status}
          isPremierTenant={isPremierTenant}
        />
      )
    },
    {
      key: UserListKeys.UserName,
      name: "User Name",
      maxWidth: 250,
      minWidth: 200,
      onRender: (item: PltUser) => item.username,
      isSorted: true,
      isSortedDescending: getIsSortedDescending(UserListKeys.UserName),
      onColumnClick: onColumnClick(UserListKeys.UserName)
    },
    {
      key: UserListKeys.Email,
      name: "Email",
      maxWidth: 250,
      minWidth: 200,
      onRender: (item: PltUser) =>
        item.communications?.find(c => c.type === ContactCommsType.Email)
          ?.value,
      isSorted: true,
      isSortedDescending: getIsSortedDescending(UserListKeys.Email),
      onColumnClick: onColumnClick(UserListKeys.Email)
    },
    {
      key: UserListKeys.HasBpId,
      name: "Has Bp ID",
      maxWidth: 75,
      minWidth: 75,
      onRender: (item: PltUser) => (
        <TextBadge
          badgeSize={TextBadgeSize.small}
          badgeColor={
            item.bpIdUserId ? TextBadgeColor.green : TextBadgeColor.red
          }
          styles={{ root: { width: 75 } }}
        >
          {item.bpIdUserId ? "Yes" : "No"}
        </TextBadge>
      )
    },
    {
      key: UserListKeys.HasExternalId,
      name: "Has Ext. ID",
      maxWidth: 75,
      minWidth: 75,
      onRender: (item: PltUser) => (
        <TextBadge
          badgeSize={TextBadgeSize.small}
          badgeColor={
            item?.externalId === undefined
              ? TextBadgeColor.red
              : TextBadgeColor.green
          }
          styles={{ root: { width: 75 } }}
        >
          {item.externalId ? "Yes" : "No"}
        </TextBadge>
      )
    }
  ];

  return (
    <Stack styles={{ root: { position: "relative", width: "100%" } }} grow>
      <ScrollablePane>
        <ShimmeredDetailsList
          stickyHeader
          items={data}
          columns={columns}
          setKey="plt_users_id"
          errorMessage={error?.message}
          onShouldVirtualize={() => true}
          enableShimmer={isLoading}
          detailsListStyles={{
            contentWrapper: {}
          }}
          onRenderRow={(props, renderDefault) => {
            if (props && renderDefault) {
              return (
                <ExpandableRow
                  id={props.item.id}
                  expandedIds={expandedIds}
                  toggleExpandedIds={toggleExpandedIds}
                  ExpandableComponent={
                    <AdditionalUserInformationRow user={props.item} />
                  }
                  rowProps={props}
                />
              );
            }
            return null;
          }}
        />
        {userToInvite && (
          <InviteFormDialog
            tenantId={userToInvite.tenantId}
            userId={userToInvite.id}
            bpIdUserId={userToInvite.bpIdUserId}
            firstName={userToInvite.firstName}
            lastName={userToInvite.lastName}
            onDismiss={() => setInviteUserId(undefined)}
          />
        )}
      </ScrollablePane>
    </Stack>
  );
};
